import type { AnchorHandle } from '@/lib/elements/types/anchor-handle'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { uid } from '@/components/app/editor/tools/shared/helpers/uid'
import { splitCubicAt } from '@/lib/elements/bezier-utils'
import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import { deepClonePoints } from './anchor-points'
import { shiftSubpathsAfterInsert, subpathIndexAt, subpathWrapIndex } from './subpath-ranges'

/** A split-off control point, or null when it collapsed onto its anchor (within 0.01). */
function keepDistinctHandle(handle: AnchorHandle, anchor: PenAnchorPoint): AnchorHandle | null {
  return Math.abs(handle.x - anchor.x) > 0.01 || Math.abs(handle.y - anchor.y) > 0.01
    ? handle
    : null
}

/**
 * Add a node by splitting the segment that starts at `segmentIndex` at parameter `t`: a corner
 * point on a straight segment, or a smooth point with `splitCubicAt` handles on a curve.
 */
export function insertNodeAtSegment(
  pts: PenAnchorPoint[],
  subpaths: PenSubpathRange[],
  segmentIndex: number,
  t: number
): { points: PenAnchorPoint[]; subpaths: PenSubpathRange[] } {
  const subpathIdx = subpathIndexAt(subpaths, segmentIndex)
  const sp = subpaths[subpathIdx] ?? { start: 0, end: pts.length, isClosed: false }
  const a = pts[segmentIndex]
  const b = pts[subpathWrapIndex(sp, segmentIndex, 1)]

  const p0 = { x: a.x, y: a.y }
  const cp1 = a.handleOut ?? p0
  const cp2 = b.handleIn ?? { x: b.x, y: b.y }
  const p3 = { x: b.x, y: b.y }
  const isLine = !a.handleOut && !b.handleIn

  const updated = deepClonePoints(pts)

  if (isLine) {
    // Split a line segment
    const nx = a.x + t * (b.x - a.x)
    const ny = a.y + t * (b.y - a.y)
    const newPt: PenAnchorPoint = {
      id: uid(),
      x: nx,
      y: ny,
      type: 'corner',
      handleIn: null,
      handleOut: null,
    }

    updated.splice(segmentIndex + 1, 0, newPt)
  } else {
    const { left, right } = splitCubicAt(p0, cp1, cp2, p3, t)

    // left: [p0, l_cp1, l_cp2, split]
    // right: [split, r_cp1, r_cp2, p3]
    const splitPt: PenAnchorPoint = {
      id: uid(),
      x: left[3].x,
      y: left[3].y,
      type: 'smooth',
      handleIn: left[2],
      handleOut: right[1],
    }

    // Update existing anchors' handles
    const origHandleOut = left[1]
    const origHandleIn = right[2]

    updated[segmentIndex] = {
      ...updated[segmentIndex],
      handleOut: keepDistinctHandle(origHandleOut, a),
    }
    const bIdx = subpathWrapIndex(sp, segmentIndex, 1)

    updated[bIdx] = {
      ...updated[bIdx],
      handleIn: keepDistinctHandle(origHandleIn, b),
    }
    updated.splice(segmentIndex + 1, 0, splitPt)
  }

  return {
    points: updated,
    subpaths: shiftSubpathsAfterInsert(subpaths, subpathIdx, segmentIndex + 1),
  }
}
